import React, { useEffect, useRef, useState } from "react";
import { get_all_projects } from "../../services/projectService";
import type { ProjectItem } from "../../types/project";
import useChatbotDrawer from "../../store/useChatbotDrawer";
import { project_search } from "../../assets/images/Images";
import styles from "./ChatDrawer/chatDrawer.module.css"
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";

interface ProjectDropdownProps {
  onChange?: (projectId: string, projectName: string, subtitle?: string) => void;
  initialProjectId?: string;
  showDropdown: boolean;
  setShowDropdown: React.Dispatch<React.SetStateAction<boolean>>;
  isLastMessage?: boolean;
  AIAssistantName?: string;
  agentData?: ProjectItem[]; // Optional agent data to use instead of fetching
}

const ProjectDropdown: React.FC<ProjectDropdownProps> = ({
  onChange,
  initialProjectId,
  showDropdown,
  setShowDropdown,
  isLastMessage,
  AIAssistantName = "Elise AI",
  agentData,
}) => {
  const [selectedProject, setSelectedProject] = useState<string>("");
  const [dropdownSearch, setDropdownSearch] = useState<string>("");
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const [lastInteraction, setLastInteraction] = useState<"mouse" | "keyboard">(
    "keyboard"
  );

  const globalState = useSelector((state: any) => state.globalState);
  const isTeacher = globalState.role === "Teacher";

  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  const {
    data: projectsResponse,
    isLoading,
    // isError,
  } = useQuery<{
    projects: ProjectItem[];
    isNewFormat: boolean;
    originalResponse: any;
  }>({
    queryKey: ["get_all_projects_for_ai_chatbotdropdown"],
    queryFn: () =>
      get_all_projects({
        filters: { page: 1, limit: 1000, search: "" },
      }),
    staleTime: Infinity,
    gcTime: Infinity, // v5: renamed from cacheTime
    refetchOnWindowFocus: false,
    retry: 2,
    enabled: !agentData, // Skip API call if agentData is provided
  });

  // Use agentData if provided, otherwise use projectsResponse
  const effectiveProjects = agentData || projectsResponse?.projects || [];

  const clearSearch = () => {
    setDropdownSearch("");
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const buttonElement = document.getElementById(
        "ai-project-dropdown-toggle-button"
      );

      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(target) &&
        buttonElement &&
        !buttonElement.contains(target)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [setShowDropdown]);

  // Helper function to get project name for display (key for ProjectEntity, project_name for Project)
  const getProjectName = (project: ProjectItem): string => {
    if ('key' in project) {
      // New structure (ProjectEntity) - display the key
      return project.key;
    } else {
      // Old structure (Project)
      return typeof project.project_name === 'string' ? project.project_name : '';
    }
  };

  // Helper function to get project value for WebSocket (value for ProjectEntity, _id for Project)
  const getProjectValue = (project: ProjectItem): string => {
    if ('value' in project) {
      // New structure (ProjectEntity) - use value (can be string or number)
      return String(project.value);
    } else {
      // Old structure (Project)
      return project._id;
    }
  };

  // Helper function to get project ID for matching (value for ProjectEntity, _id for Project)
  const getProjectId = (project: ProjectItem): string => {
    if ('value' in project) {
      // New structure (ProjectEntity) - use value for matching
      return String(project.value);
    } else {
      // Old structure (Project)
      return project._id;
    }
  };

const getSubtitle = (project: ProjectItem): string => {
  // Access using bracket notation for hyphenated property
  const subtitle = (project as any)['sub-title'];
  return typeof subtitle === 'string' ? subtitle : '';
};

  // Filter projects based on dropdownSearch
  const filteredProjects = effectiveProjects.filter((project) => {
    const projectName = getProjectName(project);
    const subtitle = getSubtitle(project);
    return (
    typeof projectName === 'string' && projectName.toLowerCase().includes(dropdownSearch.toLowerCase()) ||
    typeof subtitle === 'string' && subtitle.toLowerCase().includes(dropdownSearch.toLowerCase())
    );
  });

  // Combine 'Ask Elise' as the first item for navigation and selection
  let allDropdownItems = [...filteredProjects];

  allDropdownItems = filteredProjects;

  // Focus input when dropdown opens
  useEffect(() => {
    if (showDropdown && inputRef.current) {
      inputRef.current.focus();
      // Set selectedProject to initialProjectId or first project on open
      if (initialProjectId !== undefined) {

        setSelectedProject(initialProjectId);
      } else if (
        effectiveProjects && Array.isArray(effectiveProjects) &&
        effectiveProjects.length > 0 && selectedProject !== ''
      ) {
        const firstProjectId = getProjectId(effectiveProjects[0]);
        if (firstProjectId) {
          setSelectedProject(firstProjectId);
        }
      } else {
        setSelectedProject("");
      }
    }
  }, [showDropdown, initialProjectId, effectiveProjects, selectedProject]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (allDropdownItems.length === 0) return;
    let newIndex = highlightedIndex;
    if (e.key === "ArrowDown") {
      setLastInteraction("keyboard");
      e.preventDefault();
      if (lastInteraction === "mouse") setLastInteraction("keyboard");
      newIndex =
        highlightedIndex < allDropdownItems.length - 1
          ? highlightedIndex + 1
          : 0;
      setHighlightedIndex(newIndex);
    } else if (e.key === "ArrowUp") {
      setLastInteraction("keyboard");
      e.preventDefault();
      if (lastInteraction === "mouse") setLastInteraction("keyboard");
      newIndex =
        highlightedIndex > 0
          ? highlightedIndex - 1
          : allDropdownItems.length - 1;
      setHighlightedIndex(newIndex);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < allDropdownItems.length) {
        const project = allDropdownItems[highlightedIndex];
        if (!project) {
          return;
        }
        const projectValue = getProjectValue(project);
        const projectName = getProjectName(project);
        useChatbotDrawer.setState({
          source: {
            projectId: projectValue,
            projectName: projectName,
          },
        });
        setShowDropdown(false);
        if (onChange) onChange(projectValue, projectName);
      }
    }
  };

  useEffect(() => {
    if (highlightedIndex >= 0 && itemRefs.current[highlightedIndex]) {
      itemRefs.current[highlightedIndex]?.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [highlightedIndex]);

  return (
    <div
      ref={dropdownRef}
      id="project-dropdown"
      className={`absolute left-0 right-0 bg-white rounded-xl shadow-lg z-50 text-sm border border-gray-100 w-56 ${isLastMessage ? "bottom-3 mb-1" : "top-2 mt-1"
        }`}
      tabIndex={0}
    >
      <div className="p-2 ">
        <div
          className="text-table-head text-sm mb-2 py-2 px-1 rounded-lg hover:bg-teal-green"
          onClick={() => {
            useChatbotDrawer.setState({
              source: {
                projectId: "",
                projectName: "Ask Elise",
              },
            });
            setShowDropdown(false);
            if (onChange) onChange("", "Ask Elise");
          }}
        >
          {AIAssistantName}
        </div>

        <div className="flex justify-between mr-4 items-center text-xs mb-2 text-table-head  ">
          <span>Courses</span>
          <span> {effectiveProjects.length}</span>
        </div>

        {/* Search input field */}
        {/* Search input field */}
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            placeholder="Search course"
            value={dropdownSearch}
            onChange={(e) => {
              setDropdownSearch(e.target.value);
              setHighlightedIndex(-1); // Only reset on search change
            }}
            onKeyDown={handleKeyDown}
            className={styles["search_course_input"]}
            autoComplete="off"
          />
          <img
            src={project_search || "/placeholder.svg"}
            alt="Search"
            className="absolute left-2 top-2 "
          />
          {/* Clear button */}
          {dropdownSearch && (
            <button
              onClick={clearSearch}
              className={styles["clear_search_course_button"]}
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>


        {/* Show the number of filtered projects */}
        <div className="text-[10px] text-gray-500 mb-2">
          Showing {filteredProjects.length} of{" "}
          {effectiveProjects.length} courses
        </div>

        {/* Dropdown content */}
        <div className="h-[100px] overflow-y-auto">
          {/* Default option for Ask Elise (no project) */}
          {/* <div
            onClick={() => {
              useChatbotDrawer.setState({
                source: {
                  projectId: "",
                  projectName: "Ask Elise",
                },
              });
              setShowDropdown(false);
              if (onChange) onChange("", "Ask Elise");
            }}
            className={`px-2 py-1 rounded-md text-xs cursor-pointer text-g-100 hover:bg-teal-green ${
              selectedProject === "" ? "bg-teal-green font-semibold" : ""
            }`}
          >
            Ask Elise
          </div> */}
          {isLoading && (
            <div className="px-2 py-1 text-xs cursor-pointer text-gray-700 hover:bg-teal-green rounded">
              Loading courses...
            </div>
          )}
          {allDropdownItems.length === 0 ? (
            <div className="text-gray-400 text-xs text-center py-3">
              No courses found
            </div>
          ) : (
            allDropdownItems.map((project, idx) => (
              <div
                ref={(el) => {
                  itemRefs.current[idx] = el;
                }}
                key={getProjectId(project)}
                onClick={() => {
                  const projectValue = getProjectValue(project);
                  const projectName = getProjectName(project);
                  const subtitle = getSubtitle(project);
                  useChatbotDrawer.setState({
                    source: {
                      projectId: projectValue,
                      projectName: projectName,
                    },
                  });
                  setShowDropdown(false);
                  if (onChange) onChange(projectValue, projectName, subtitle);
                }}
                // onMouseEnter={() => {
                //   if (lastInteraction === "mouse") {
                //     setHighlightedIndex(idx);
                //   }
                //   setLastInteraction("mouse");
                // }}
                // className={`px-2 py-1 text-xs cursor-pointer text-gray-700  rounded
                //   ${highlightedIndex === idx ? "bg-teal-green font-semibold" : ""}
                //   ${selectedProject === getProjectId(project) ? "bg-gray-200 font-bold" : ""}`.replace(
                //   /\s+/g,
                //   " "
                // )}

                  className={`px-2 py-1 text-xs cursor-pointer rounded
    hover:bg-teal-green hover:font-semibold
    ${selectedProject === getProjectId(project) ? "bg-gray-200 font-bold" : ""}`.replace(
    /\s+/g,
    " "
  )}
              >
                <div className="text-black text-[11px]">{getProjectName(project)}</div>
                {!isTeacher && ( <div className="text-[#949494] text-xs">{getSubtitle(project) || ""}</div>)}
               
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectDropdown;
